import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, IonModal, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { honapNev } from '../ber.opciok';
import { BerJuttatasApi, ftSzoveg, HONAPOK_EVVEL, szazalek, tizedes } from '../juttatas/juttatas.api';
import { JuttatasParamComponent } from '../juttatas/juttatas-param.component';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';
import { SzerkZarSavComponent } from '../../components/szerk-zar/szerk-zar-sav.component';

/**
 *  REPREZENTÁCIÓ ÉS ÜZLETI AJÁNDÉK (RLB: Számfejtés → Reprezentáció).
 *
 *  Havonta tételesen (dátum, megnevezés, érték), NEM személyhez kötve. Mindkettő egyes
 *  meghatározott juttatás: adóalap = érték × 1,18, erre SZJA és szocho (KIVA-alanynál szocho
 *  helyett a KIVA-alapba kerül). Ami a jogszabály szerint adómentes (pl. a tevékenységhez
 *  kapcsolódó, adómentes vendéglátás – ellenőrizendő), az „adómentes" jelöléssel rögzíthető:
 *  a listában benne van, közterhe nincs.
 */
@Component({
  selector: 'app-ber-reprezentacio',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, JuttatasParamComponent, SzerkZarSavComponent],
  templateUrl: './reprezentacio.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './reprezentacio.component.scss'],
})
export class ReprezentacioComponent implements OnInit, OnDestroy {
  @ViewChild('szerkModal') szerkModal: IonModal;
  @ViewChild('paramModal') paramModal: IonModal;
  static readonly TIPUSOK = [
    { value: 'reprezentacio', label: 'Reprezentáció' },
    { value: 'uzleti_ajandek', label: 'Üzleti ajándék' },
  ];
  tipusok = ReprezentacioComponent.TIPUSOK;
  evek: number[] = [];
  honapok = HONAPOK_EVVEL;
  ft = ftSzoveg;
  szazalek = szazalek;
  tizedes = tizedes;

  ho = 0;                // 0 = egész év (csak ezen a képernyőn; a közös aktív hónap 1–12)
  sorok: any[] = [];
  param: any = null;
  kivas = false;
  betoltve = false;
  szerk: any = null;
  mentes = false;

  constructor(private ber: BerService, private api: BerJuttatasApi, private toast: ToastController, private alert: AlertController,
              private szerkZar: SzerkZarService) {}

  /*  SZERKESZTÉSI ZÁR (components/szerk-zar) a meglévő tételre: egyszerre egy ember szerkesztheti.
   *  A sorszám a késve érkező foglalást dobja el, ha a szerkesztőt közben bezárták. */
  zarSor: SzerkZar | null = null;
  private zarSorsz = 0;
  private async zarFoglal(s: any) {
    const n = ++this.zarSorsz;
    this.zarSor?.felenged(); this.zarSor = null;
    const z = await this.szerkZar.foglal('ber', 'ber_juttatas', s.id, { ujratolt: () => this.sorUjratolt(s) });
    if (n !== this.zarSorsz) { z?.felenged(); return; }
    this.zarSor = z;
  }
  zarEngedi() { this.zarSorsz++; this.zarSor?.felenged(); this.zarSor = null; }
  /** Friss adat a nyitott szerkesztőbe (a tétel évének juttatásaiból). */
  private async sorUjratolt(s: any) {
    const ev = Number(s.ev) || Number(String(s.datum || '').slice(0, 4)) || this.aktiv.ev;
    const sorok: any[] = await this.api.get(`/juttatas/${s.ugyfel_id || this.aktiv.ugyfelId}?ev=${ev}`);
    const friss = sorok.find((x) => x.id === s.id);
    if (this.szerk?.id !== s.id) return;
    if (friss) this.szerk = { ...friss };
    else { this.hiba('A tételt közben törölték.'); await this.szerkModal.dismiss(); }
  }
  ngOnDestroy() { this.zarEngedi(); }

  async ngOnInit() {
    await this.ber.init();
    const ev = new Date().getFullYear();
    this.evek = [ev + 1, ev, ev - 1, ev - 2, ev - 3];
    this.ho = this.aktiv.ho;
    await this.betolt();
  }

  get aktiv() { return this.ber.aktiv; }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { this.ho = ho; if (ho) await this.ber.setAktiv({ ho }); await this.betolt(); }
  get idoszak(): string { return this.ho ? `${this.aktiv.ev}. ${honapNev(this.ho).toLowerCase()}` : `${this.aktiv.ev}. év`; }

  async betolt() {
    this.betoltve = false;
    if (!this.aktiv.ugyfelId) return;
    try {
      const [sorok, p]: any[] = await Promise.all([
        this.api.get(`/juttatas/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}&ho=${this.ho}&tipus=reprezentacio,uzleti_ajandek`),
        this.api.get(`/param/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}`),
      ]);
      this.sorok = sorok || [];
      this.kivas = !!p.kivas;
      this.param = Object.fromEntries((p.parameterek || []).map((x: any) => [x.kulcs, x.ertek]));
      this.betoltve = true;
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni.'); }
  }

  get osszesen() {
    const o = { ertek: 0, adoalap: 0, szja: 0, szocho: 0, kiva_alap: 0 };
    for (const s of this.sorok) for (const k of Object.keys(o) as (keyof typeof o)[]) o[k] += Number(s[k]) || 0;
    return o;
  }
  tipusOssz(t: string): number { return this.sorok.filter((s) => s.tipus === t).reduce((a, s) => a + (Number(s.ertek) || 0), 0); }

  /* ── szerkesztő ablak ── */
  uj() {
    this.zarEngedi();
    const ho = this.ho || this.aktiv.ho;
    const ma = new Date();
    const nap = ma.getFullYear() === this.aktiv.ev && ma.getMonth() + 1 === ho ? ma.getDate() : 1;
    this.szerk = { tipus: 'reprezentacio', datum: `${this.aktiv.ev}-${String(ho).padStart(2, '0')}-${String(nap).padStart(2, '0')}`,
      megnevezes: '', ertek: null, adomentes: false, megjegyzes: '' };
    this.szerkModal.present();
  }
  modosit(s: any) { this.szerk = { ...s }; this.szerkModal.present(); this.zarFoglal(s); }
  elonezet(s: any) {
    if (!s || s.adomentes) return { adoalap: 0, szja: 0, szocho: 0 };
    const adoalap = Math.round((Number(s.ertek) || 0) * (this.param?.adoalap_szorzo ?? 1.18));
    return { adoalap, szja: Math.round(adoalap * (this.param?.szja_kulcs ?? 0.15)), szocho: this.kivas ? 0 : Math.round(adoalap * (this.param?.szocho_kulcs ?? 0.13)) };
  }

  async ment() {
    const s = this.szerk;
    if (this.zarSor && !this.zarSor.irhato) return;
    if (!s.datum || !(Number(s.ertek) > 0)) { this.hiba('A dátum és az érték kötelező.'); return; }
    const b = { tipus: s.tipus, datum: s.datum, megnevezes: s.megnevezes, ertek: Number(s.ertek), adomentes: !!s.adomentes, megjegyzes: s.megjegyzes };
    this.mentes = true;
    try {
      if (s.id) await this.api.put(`/juttatas/sor/${s.id}`, b);
      else await this.api.post(`/juttatas/${this.aktiv.ugyfelId}`, b);
      await this.szerkModal.dismiss();
      this.ok('Mentve.');
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Mentési hiba.'); }
    finally { this.mentes = false; }
  }

  async torol(s: any) {
    const a = await this.alert.create({
      header: 'Tétel törlése', message: `${s.datum} · ${s.megnevezes} (${ftSzoveg(s.ertek)} Ft)`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive', handler: async () => {
        try { await this.api.del(`/juttatas/sor/${s.id}`); this.ok('Törölve.'); await this.betolt(); }
        catch (e: any) { this.hiba(e?.error?.error || 'A törlés nem sikerült.'); }
      } }],
    });
    await a.present();
  }

  async ujraszamol() {
    try {
      const r: any = await this.api.post(`/juttatas/${this.aktiv.ugyfelId}/ujraszamol?ev=${this.aktiv.ev}&ho=${this.ho}`, {});
      this.ok(`${r.tetelek} tétel újraszámolva az aktuális paraméterekkel.`);
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'Az újraszámolás nem sikerült.'); }
  }

  async nyomtat() {
    const oszlopok = [
      { k: 'datum', c: 'Dátum' }, { k: 'tipus_cim', c: 'Típus' }, { k: 'megnevezes', c: 'Megnevezés', szeles: true },
      { k: 'ertek', c: 'Érték', num: true }, { k: 'adoalap', c: 'Adóalap', num: true }, { k: 'szja', c: 'SZJA', num: true },
      { k: this.kivas ? 'kiva_alap' : 'szocho', c: this.kivas ? 'KIVA-alapba' : 'Szocho', num: true }, { k: 'megjegyzes', c: 'Megjegyzés', szeles: true },
    ];
    const o: any = this.osszesen;
    await this.api.nyomtat('Reprezentáció és üzleti ajándék', [['Időszak', this.idoszak]], oszlopok, this.sorok,
      (s, c) => (c.num ? ftSzoveg(s[c.k]) : (c.k === 'megjegyzes' ? [s.adomentes ? 'adómentes' : '', s.megjegyzes || ''].filter(Boolean).join(' · ') : String(s[c.k] ?? ''))),
      (c) => (c.num ? ftSzoveg(o[c.k]) : ''));
  }

  async parameterek() { await this.paramModal.present(); }
  async paramMentve() { await this.paramModal.dismiss(); await this.betolt(); }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'danger' })).present(); }
}
